// ==========================================
// Buddy progress — XP, levels, streak, quests, badges (3d XP ECONOMY)
// ==========================================
// Gamification stays quiet by default: this service owns the numbers, and the only
// permanent chrome that reads from it is the 3px XP rail, the level number in the
// header and the streak pill (<= 46px total, per the 2d guardrail). Everything else
// it emits is ephemeral — a floating +XP chip or an inline milestone banner.
// ==========================================

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { BuddyProgress, GrowthStage, RewardChip, XpAward } from '../models/buddy.types';
import {
  BONUS_DROP_MULTIPLIERS,
  BONUS_DROP_ODDS,
  GROWTH_STAGES,
  HABITAT_PROPS,
  LEVEL_THRESHOLDS,
  MAX_TABLED_LEVEL,
  POST_TABLE_STEP,
  POST_TABLE_STEP_GROWTH,
  STORAGE_KEY_PROGRESS,
  STREAK_MULTIPLIER_MAX,
  STREAK_MULTIPLIER_MIN,
  STREAK_MULTIPLIER_PEAK_DAY,
  STREAK_NUDGE_CAP_PER_DAY,
  XP_CHAT_EXCHANGE,
  XP_CHAT_SOFT_CAP_PER_DAY,
  XP_DAILY_FIRST_OPEN,
  XP_DEFINE_WORKFLOW,
  XP_RUN_WORKFLOW,
  XP_WEEKLY_CHALLENGE,
} from '../models/buddy.constants';
import { buildMockProgress } from './buddy-mock.data';

/** Cumulative XP needed to reach `level`, extending past the tabled range. */
export function thresholdForLevel(level: number): number {
  if (level <= 1) {
    return 0;
  }
  if (level <= MAX_TABLED_LEVEL) {
    return LEVEL_THRESHOLDS[level];
  }
  let total = LEVEL_THRESHOLDS[MAX_TABLED_LEVEL];
  let step = POST_TABLE_STEP;
  for (let l = MAX_TABLED_LEVEL + 1; l <= level; l++) {
    total += step;
    step += POST_TABLE_STEP_GROWTH;
  }
  return total;
}

export function levelForXp(xp: number): number {
  let level = 1;
  while (thresholdForLevel(level + 1) <= xp) {
    level += 1;
    // Defensive stop; the curve is monotonic so this should never bind.
    if (level > 999) {
      break;
    }
  }
  return level;
}

/** Streak multiplier ramps 1.0x (day 1) to 2.0x (day 30). Day 12 lands on 1.4x. */
export function multiplierForStreak(days: number): number {
  if (days <= 1) {
    return STREAK_MULTIPLIER_MIN;
  }
  const capped = Math.min(days, STREAK_MULTIPLIER_PEAK_DAY);
  const span = STREAK_MULTIPLIER_MAX - STREAK_MULTIPLIER_MIN;
  const ratio = (capped - 1) / (STREAK_MULTIPLIER_PEAK_DAY - 1);
  return Math.round((STREAK_MULTIPLIER_MIN + span * ratio) * 10) / 10;
}

export function stageForLevel(level: number): { stage: GrowthStage; label: string; antenna: string } {
  const found = GROWTH_STAGES.find((s) => level >= s.minLevel && level <= s.maxLevel);
  const def = found ?? GROWTH_STAGES[GROWTH_STAGES.length - 1];
  return { stage: def.stage, label: def.label, antenna: def.antenna };
}

@Injectable({ providedIn: 'root' })
export class BuddyProgressService {
  private readonly progressSubject = new BehaviorSubject<BuddyProgress>(this.load());
  private readonly awardSubject = new Subject<XpAward>();

  readonly progress: Observable<BuddyProgress> = this.progressSubject.asObservable();
  /** Fires whenever XP lands, so the rail can animate and a chip can float. */
  readonly awards: Observable<XpAward> = this.awardSubject.asObservable();

  get current(): BuddyProgress {
    return this.progressSubject.value;
  }

  // ------------------------------------------
  // Awards
  // ------------------------------------------

  /** +5 per chat exchange, stops paying past 50 XP of chat in a day. */
  awardChatExchange(): XpAward | null {
    const p = this.current;
    if (p.chatXpToday >= XP_CHAT_SOFT_CAP_PER_DAY) {
      return null;
    }
    const grant = Math.min(XP_CHAT_EXCHANGE, XP_CHAT_SOFT_CAP_PER_DAY - p.chatXpToday);
    return this.grant(grant, 'Chat exchange', { chatXpToday: p.chatXpToday + grant }, false);
  }

  /** +20 x streak multiplier, with a ~1-in-8 chance of a 2-3x surprise drop. */
  awardWorkflowRun(workflowName: string): XpAward {
    const p = this.current;
    const bonus = this.rollBonus();
    const base = Math.round(XP_RUN_WORKFLOW * p.multiplier);
    const total = Math.round(base * bonus.multiplier);
    const quest = p.quest.done < p.quest.total ? { ...p.quest, done: p.quest.done + 1 } : p.quest;

    return this.grant(total, workflowName, { quest }, bonus.hit, bonus.multiplier);
  }

  /** +50 for teaching BB something new. */
  awardDefineWorkflow(): XpAward {
    return this.grant(XP_DEFINE_WORKFLOW, 'New workflow defined', {}, false);
  }

  /** +100 when the weekly challenge closes out. */
  awardWeeklyChallenge(): XpAward {
    const p = this.current;
    const challenge = { ...p.challenge, done: p.challenge.total };
    return this.grant(XP_WEEKLY_CHALLENGE, 'Weekly challenge', { challenge }, false);
  }

  /**
   * +15 the first time Buddy is opened on a given day, and rolls the streak forward.
   * Returns null on subsequent opens the same day.
   */
  awardDailyOpen(): XpAward | null {
    const today = new Date().toISOString().slice(0, 10);
    const p = this.current;
    if (p.lastOpenedOn === today) {
      return null;
    }
    const streakDays = p.lastOpenedOn === '' ? p.streakDays : p.streakDays + 1;
    return this.grant(XP_DAILY_FIRST_OPEN, 'Daily first open', {
      lastOpenedOn: today,
      streakDays,
      multiplier: multiplierForStreak(streakDays),
      chatXpToday: 0,
      nudgesToday: 0,
    }, false);
  }

  // ------------------------------------------
  // Streak nudges — capped at 2/day (2d guardrail)
  // ------------------------------------------

  canNudge(): boolean {
    return this.current.nudgesToday < STREAK_NUDGE_CAP_PER_DAY;
  }

  recordNudge(): void {
    this.patch({ nudgesToday: this.current.nudgesToday + 1 });
  }

  // ------------------------------------------
  // Internals
  // ------------------------------------------

  private rollBonus(): { hit: boolean; multiplier: number } {
    const hit = Math.floor(Math.random() * BONUS_DROP_ODDS) === 0;
    if (!hit) {
      return { hit: false, multiplier: 1 };
    }
    const pick = BONUS_DROP_MULTIPLIERS[Math.floor(Math.random() * BONUS_DROP_MULTIPLIERS.length)];
    return { hit: true, multiplier: pick };
  }

  private grant(
    xp: number,
    label: string,
    patch: Partial<BuddyProgress>,
    bonus: boolean,
    bonusMultiplier = 1
  ): XpAward {
    const before = this.current;
    const totalXp = before.xp + xp;
    const newLevel = levelForXp(totalXp);
    const leveledUp = newLevel > before.level;

    const floor = thresholdForLevel(newLevel);
    const ceil = thresholdForLevel(newLevel + 1);
    const stage = stageForLevel(newLevel);

    const next: BuddyProgress = {
      ...before,
      ...patch,
      xp: totalXp,
      level: newLevel,
      stage: stage.stage,
      stageLabel: stage.label,
      xpIntoLevel: totalXp - floor,
      xpForLevel: ceil - floor,
      habitat: leveledUp
        ? HABITAT_PROPS.map((prop) => ({ ...prop, unlocked: prop.unlockLevel <= newLevel }))
        : before.habitat,
    };

    this.commit(next);

    const reward: RewardChip = {
      xp,
      multiplier: bonus ? bonusMultiplier : before.multiplier,
      label,
      bonus,
    };
    const award: XpAward = { reward, leveledUp, newLevel };
    this.awardSubject.next(award);
    return award;
  }

  private patch(patch: Partial<BuddyProgress>): void {
    this.commit({ ...this.current, ...patch });
  }

  private commit(next: BuddyProgress): void {
    this.progressSubject.next(next);
    this.saveBuddyState(next);
  }

  /**
   * API-SWAP: replace with PUT /api/buddy/progress.
   * Until then progress survives reloads via localStorage so the streak and level
   * read as continuous during UAT.
   */
  private saveBuddyState(progress: BuddyProgress): void {
    try {
      localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(progress));
    } catch {
      // Non-fatal: progress is cosmetic until the backend owns it.
    }
  }

  /** API-SWAP: replace with GET /api/buddy/progress. */
  private load(): BuddyProgress {
    const seed = buildMockProgress();
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PROGRESS);
      if (!raw) {
        return seed;
      }
      const stored = JSON.parse(raw) as Partial<BuddyProgress>;
      // Merge onto the seed so newly added fields never come back undefined.
      return { ...seed, ...stored };
    } catch {
      return seed;
    }
  }

  /** Escape hatch for UAT — resets to the mockup's seeded state. */
  reset(): void {
    this.commit(buildMockProgress());
  }
}
